<script setup lang="ts">
  import { computed, onMounted } from 'vue'
  import type { NtpAdEvent } from '../../composables/analytics/types'
  import { useAnalytics } from '../../composables/analytics/useAnalytics'
  import { getIndexOfPlacement } from '../../utils/ad-ops/adFunctions'

  interface AssertiveAdAttributes {
    'data-render-registered': boolean
    // 'data-ad-instance-id': string
  }
  export interface AssertiveProps {
    placement: string
    adHeight?: number
    adWidth?: number
    tilePosition?: number
    visibilityThresholdInPercentage?: number
    visibilityTimeoutInMs?: number
    stickyClass?: string
  }

  /**
   * Props for the Assertive component
   * @property placement - The placement identifier for the ad
   * @property tilePosition - Optional position of the tile in a list
   * @property visibilityThresholdInPercentage - Optional percentage of the element that must be visible
   * @property visibilityTimeoutInMs - Optional timeout in milliseconds before rendering when visible
   * @property adHeight - Height of the ad in pixels
   * @property adWidth - Width of the ad in pixels
   * @property  stickyClass - Optional sticky class for the ad
   */

  const props = withDefaults(defineProps<AssertiveProps>(), {
    adHeight: 304,
    adWidth: 300,
    visibilityThresholdInPercentage: 1, // 0 means no threshold, always trigger callback
    visibilityTimeoutInMs: 0, // 0 means no timeout
    tilePosition: -1,
    stickyClass: '',
  })

  const { track } = useAnalytics()
  const { ayLoaded } = await useAssertiveYield()
  const placementEl = useTemplateRef<HTMLElement | null>('placementEl')
  const placementClassName = ref<string | null>(props.placement)
  const { renderWhenCurrentElementIsVisible } = useAdFunctions()

  const renderAYElement = ref<boolean>(false)
  const adUnitPath = ref<string>('')
  const placementAttributes = computed<AssertiveAdAttributes>(() => {
    return {
      'data-render-registered': false,
    }
  })

  function getEventData(): NtpAdEvent {
    const eventData: NtpAdEvent = {
      ntp_tile_position: props.tilePosition,
      ntp_ad_brand: 'programmatic',
      ntp_ad_provider: 'programmatic',
      ntp_creative_id: adUnitPath.value,
      ntp_placement_name: props.placement,
    }

    return eventData
  }

  function handleAdClick() {
    track('NtpAd_Clicked', getEventData())
  }

  function attachClickEventToAnchorTags(element: Element) {
    const anchorTag = element.querySelector('a')

    if (anchorTag && !anchorTag.getAttribute('data-click-registered')) {
      anchorTag.setAttribute('data-click-registered', 'true')
      anchorTag.addEventListener('click', handleAdClick)
    }
  }

  function detachClickEventFromAnchorTags(element: Element) {
    const registeredAnchors = element.querySelectorAll(
      'a[data-click-registered="true"]'
    )
    registeredAnchors.forEach((anchor) => {
      anchor.removeEventListener('click', handleAdClick)
    })
  }

  const renderAYAd = () => {
    if (
      !placementEl.value ||
      placementEl.value?.getAttribute('data-render-registered') === 'true' ||
      renderAYElement.value
    )
      return
    renderAYElement.value = true
    placementEl.value?.setAttribute('data-render-registered', 'true')

    const index = getIndexOfPlacement(props.placement, placementEl.value)

    placementClassName.value =
      index > 0
        ? `${props.placement} ${props.placement}-${index}`
        : props.placement

    window.ayManagerEnv = window.ayManagerEnv || { cmd: [] }
    window.ayManagerEnv.cmd.push(function () {
      window.ayManagerEnv.display(props.placement)
    })
    attachClickEventToAnchorTags(placementEl.value)
    track('NtpAd_Rendered', getEventData())
  }

  onMounted(async () => {
    await ayLoaded
    renderWhenCurrentElementIsVisible(
      placementEl.value,
      props.visibilityThresholdInPercentage,
      props.visibilityTimeoutInMs,
      renderAYAd
    )
  })

  onUnmounted(() => {
    if (placementEl.value) {
      detachClickEventFromAnchorTags(placementEl.value)
      window.ayManagerEnv = window.ayManagerEnv || { cmd: [] }
    }
  })
</script>

<template>
  <div
    ref="placementEl"
    :class="placementClassName"
    :data-ay-manager-id="props.placement"
    :style="{ width: `${props.adWidth}px`, height: `${props.adHeight}px` }"
    v-bind="placementAttributes"
  />
</template>
